import React from 'react';
import { ArrowRight, Brain, Zap, Target, Binary, Menu, X } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import { Link } from 'react-router-dom';

const navigation = [
  { name: 'Processus', href: '/processus' },
  { name: 'Enjeux', href: '/#problems' },
  { name: 'Solutions', href: '/#solutions' },
  { name: 'Marketing', href: '/#marketing' },
  { name: 'Témoignages', href: '/#testimonials' },
  { name: 'Contact', href: '/#contact' },
];

const clients = [
  "Procter & Gamble", "Louis Vuitton", "Timberland", "Sephora", "Europcar", 
  "Peugeot", "Toshiba", "First People Tech.", "Black & Decker", "Le Cnam", "FRAM", "Belambra", 
  "Sofinco", "Asphalte", "Rhum Plantation", "Rhum HSE", "N1 Immobilier", "Blue Cult", "Maestria Couture", "Siapoc", "Getaround"
];

const services = [
  {
    icon: Brain,
    title: "Stratégie IA & Marketing",
    description: "Des campagnes ultra-ciblées, intelligentes et évolutives."
  },
  {
    icon: Zap,
    title: "Automatisation intelligente",
    description: "Nous transformons vos flux marketing pour plus de productivité et moins de friction."
  },
  {
    icon: Target,
    title: "Optimisation continue",
    description: "Nos campagnes s'optimisent en temps réel selon vos KPI."
  },
  {
    icon: Binary,
    title: "Solutions sur mesure",
    description: "Chaque marque est unique, nos architectures digitales aussi."
  }
];

const pillars = [
  {
    title: "Pilotée par la donnée",
    description: "Chaque décision stratégique est nourrie par des insights précis, actualisés en continu."
  },
  {
    title: "Imaginée pour durer",
    description: "Nous construisons des fondations solides, évolutives, pensées pour votre croissance future."
  },
  {
    title: "Activée en co-création",
    description: "Nos clients font partie du processus, pas de simples spectateurs."
  }
];

export default function Processus() {
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto px-8 py-4 flex justify-between items-center">
          
          {/* Logo + Claim */}
          <div className="flex flex-col leading-tight">
            <Link to="/" className="flex flex-col">
              <div className="flex items-baseline">
                <span className="text-2xl font-bold text-white">nova</span>
                <span className="text-2xl font-bold text-blue-500">communication</span>
              </div>
              <span className="text-xs text-gray-400 tracking-wide">
                — by KEROS MANAGEMENT
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className="text-gray-300 hover:text-blue-400 transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-300 hover:text-white transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-black/95 border-b border-gray-800">
            <div className="container mx-auto px-8 py-4">
              <ul className="space-y-4">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="block text-gray-300 hover:text-blue-400 transition-colors duration-200 py-2"
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: "url('https://res.cloudinary.com/doo9fgw4x/image/upload/v1744917888/Nova-processus_rn1f9n.jpg?auto=format&fit=crop&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Une méthode.<br /> Une exigence.<br />Une promesse de résultats.
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
              Chez Nova, chaque projet est le fruit d'un processus rigoureux, créatif et piloté par les données.
              Nous ne livrons pas un service, mais une trajectoire vers la performance.
            </p>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Culture de l'exigence</h2>
            <p className="text-xl text-gray-300">
              Expérience, créativité, innovation. Ces trois piliers structurent chaque projet que nous menons.
              Des collaborations avec des marques qui partagent notre quête d'excellence.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {clients.map((client, index) => (
              <div 
                key={index}
                className="card text-center"
              >
                <p className="text-sm font-medium">{client}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section - Redesigned for better harmony */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">
              Une approche intégrée, orientée impact
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Text Content */}
              <div className="space-y-8">
                <div className="space-y-6">
                  <p className="text-xl text-gray-300 leading-relaxed">
                    Du site web à la campagne automatisée, nous concevons des 
                    <span className="text-blue-400 font-semibold"> écosystèmes digitaux complets</span>.
                  </p>
                  
                  <p className="text-xl text-gray-300 leading-relaxed">
                    Design, développement, contenu, IA – tout est pensé pour résoudre des 
                    problèmes complexes avec des solutions 
                    <span className="text-blue-400 font-semibold"> simples, mesurables, élégantes</span>.
                  </p>
                  
                  <div className="bg-gradient-to-r from-blue-500/10 to-transparent border-l-4 border-blue-500 pl-6 py-4">
                    <p className="text-xl font-semibold text-white">
                      Nous ne faisons pas "pour vous", mais "avec vous" – en tant que partenaire stratégique.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium">
                    Stratégie Data-Driven
                  </div>
                  <div className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium">
                    IA & Automation
                  </div>
                  <div className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium">
                    ROI Mesurable
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="relative">
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src="https://res.cloudinary.com/doo9fgw4x/image/upload/v1749453542/nova_communication-X-gen_jraiwc.jpg"
                    alt="Digital Ecosystem"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {/* Overlay content */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-gray-800">
                      <p className="text-white font-semibold mb-2">Innovation & Créativité</p>
                      <p className="text-gray-300 text-sm">
                        Chaque projet combine expertise technique et vision créative pour des résultats exceptionnels.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-xl"></div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/10 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ce que nous construisons</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="card group"
              >
                <service.icon className="w-12 h-12 text-blue-400 mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-semibold mb-4 group-hover:text-blue-400 transition-colors duration-300">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Difference Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Une méthode qui conjugue rigueur et intuition</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <div 
                key={index}
                className="card"
              >
                <h3 className="text-xl font-semibold mb-4">{pillar.title}</h3>
                <p className="text-gray-300">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Vous voulez aller plus loin ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Parlons de vos enjeux et découvrons ensemble comment transformer votre vision en réalité.
          </p>
          <button 
            onClick={() => setIsContactModalOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center gap-2 mx-auto transition-all hover:scale-105"
          >
            Demander une consultation
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold mb-4">Nova Communication</h3>
              <p className="text-gray-400">Votre partenaire en Marketing digital et IA</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="text-gray-400 space-y-2">
                <li>contact@nova-communication.com</li>
                <li>+33 189 480 425</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="text-gray-400 space-y-2">
                <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
                <li>Politique de confidentialité</li>
                <li>CGV</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Suivez-nous</h4>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  LinkedIn
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Twitter
                </a>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>© 2025 Nova Communication. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}